import { studyLiteralLabels } from './studyLiteralLabels';
import { useContext } from 'react';
import { I18nContext } from './I18nProvider';
import type { Locale } from '@/types';

// Only system labels/statuses go through this function. User titles and files do not.
const labels: Record<string, readonly [string, string]> = {
  'Связь с эдвайзером': ['Эдвайзермен байланыс', 'Contact your advisor'],
  Бакалавриат: ['Бакалавриат', 'Bachelor’s degree'],
  Магистратура: ['Магистратура', 'Master’s degree'],
  Докторантура: ['Докторантура', 'Doctoral studies'],
  '4 курс · 1 учебный год': ['4-курс · 1 оқу жылы', 'Year 4 · 1 academic year'],
  '2 года · 4 семестра': ['2 жыл · 4 семестр', '2 years · 4 semesters'],
  '3 года · 6 семестров': ['3 жыл · 6 семестр', '3 years · 6 semesters'],
  Закрыто: ['Жабық', 'Closed'],
  Открыто: ['Ашық', 'Open'],
  'Не сдано': ['Тапсырылмаған', 'Not submitted'],
  'Нужно выполнить': ['Орындау қажет', 'To do'],
  Просрочено: ['Мерзімі өткен', 'Overdue'],
  'Научная работа': ['Ғылыми жұмыс', 'Research work'],
  Занятие: ['Сабақ', 'Lesson'],
  'Отправить версию': ['Нұсқаны жіберу', 'Submit version'],
  'Не удалось отправить работу': ['Жұмысты жіберу мүмкін болмады', 'Could not submit the work'],
  'Не начато': ['Басталмаған', 'Not started'],
  Черновик: ['Жоба нұсқасы', 'Draft'],
  'Отправлено на проверку': ['Тексеруге жіберілді', 'Submitted for review'],
  'Требуются исправления': ['Түзету қажет', 'Changes required'],
  Принято: ['Қабылданды', 'Accepted'],
  Открыть: ['Ашу', 'Open'],
  Скачать: ['Жүктеп алу', 'Download'],
  Сохранить: ['Сақтау', 'Save'],
  'Отправить на проверку': ['Тексеруге жіберу', 'Submit for review'],
  Назад: ['Артқа', 'Back'],
  Далее: ['Алға', 'Next'],
  Ожидает: ['Күтілуде', 'Pending'],
  Отозвано: ['Қайтарылды', 'Revoked'],
  Истекло: ['Мерзімі аяқталды', 'Expired'],
  'Не назначен': ['Тағайындалмаған', 'Not assigned'],
  'Темы пока не назначены научным руководителем': [
    'Ғылыми жетекші әзірге тақырып ұсынған жоқ',
    'Your research supervisor has not assigned any topics yet',
  ],
  'Тема ещё не утверждена': ['Тақырып әлі бекітілмеген', 'The topic has not been approved yet'],
  'Утверждённая формулировка синхронизирована с руководителем.': [
    'Бекітілген тұжырым жетекшімен синхрондалды.',
    'The approved wording is synchronized with your supervisor.',
  ],
  Обзор: ['Шолу', 'Overview'],
  'Моя тема': ['Менің тақырыбым', 'My topic'],
  'План работы': ['Жұмыс жоспары', 'Work plan'],
  'Мои задачи': ['Менің тапсырмаларым', 'My tasks'],
  'Совместная литература': ['Ортақ әдебиет', 'Shared literature'],
  'Основная работа': ['Негізгі жұмыс', 'Main work'],
  'Образцы и документы': ['Үлгілер мен құжаттар', 'Templates and documents'],
  'Публикации / НИР': ['Жарияланымдар / ҒЗЖ', 'Publications / research'],
  'Практики / этапы': ['Практикалар / кезеңдер', 'Internships / stages'],
  'Встречи и консультации': ['Кездесулер мен кеңестер', 'Meetings and consultations'],
  'Предзащита / защита': ['Алдын ала қорғау / қорғау', 'Pre-defense / defense'],
  'AI-помощник': ['AI-көмекші', 'AI assistant'],
  'Официальные источники ENU': ['ЕҰУ ресми дереккөздері', 'Official ENU sources'],
  'Мои данные и действия': ['Менің деректерім мен әрекеттерім', 'My information and actions'],
  Дисциплины: ['Пәндер', 'Courses'],
  'Занятия и материалы': ['Сабақтар мен материалдар', 'Lessons and materials'],
  Задания: ['Тапсырмалар', 'Assignments'],
  'Оценки и РК': ['Бағалар және аралық бақылау', 'Grades and assessments'],
  Посещаемость: ['Қатысу', 'Attendance'],
  'Командные проекты': ['Топтық жобалар', 'Team projects'],
  Календарь: ['Күнтізбе', 'Calendar'],
  'Связь с преподавателем': ['Оқытушымен байланыс', 'Contact teacher'],
  'Мои работы / портфолио': ['Менің жұмыстарым / портфолио', 'My work / portfolio'],
  'Открыть задания': ['Тапсырмаларды ашу', 'Open assignments'],
  'Открыть задание': ['Тапсырманы ашу', 'Open assignment'],
  'Открыть образец': ['Үлгіні ашу', 'Open template'],
  'Скачать лекцию': ['Дәрісті жүктеп алу', 'Download lecture'],
  'Скачать практику': ['Практиканы жүктеп алу', 'Download practice'],
  'На доработке': ['Түзету қажет', 'Changes required'],
  'На проверке': ['Тексеруге жіберілді', 'Submitted for review'],
  Отправлено: ['Жіберілді', 'Submitted'],
  Принята: ['Қабылданды', 'Accepted'],
  Принят: ['Қабылданды', 'Accepted'],
  Оценено: ['Бағаланды', 'Graded'],
  Опубликовано: ['Жарияланды', 'Published'],
  'Ожидает решения': ['Шешім күтілуде', 'Awaiting decision'],
  'На рассмотрении': ['Қаралуда', 'Under review'],
  Предложено: ['Ұсынылды', 'Offered'],
  'Готово к работе': ['Жұмысқа дайын', 'Ready to work'],
  Назначено: ['Тағайындалды', 'Assigned'],
  'В работе': ['Орындалуда', 'In progress'],
  'Не использовать': ['Қолданбаңыз', 'Do not use'],
  'Работа еще не сдана': ['Жұмыс әлі тапсырылмаған', 'Work has not been submitted yet'],
  Сохранено: ['Сақталды', 'Saved'],
  'Не удалось сохранить. Проверьте соединение и повторите.': [
    'Сақтау мүмкін болмады. Байланысты тексеріп, қайталаңыз.',
    'Changes could not be saved. Check your connection and try again.',
  ],
};

const statuses: Record<string, string> = {
  NOT_STARTED: 'Не начато',
  DRAFT: 'Черновик',
  SUBMITTED: 'Отправлено на проверку',
  ON_REVIEW: 'Отправлено на проверку',
  IN_REVIEW: 'Отправлено на проверку',
  CHANGES_REQUIRED: 'Требуются исправления',
  NEEDS_REVISION: 'Требуются исправления',
  RETURNED: 'Требуются исправления',
  ACCEPTED: 'Принято',
  APPROVED: 'Принято',
};

export const studyText = (text: string, locale: Locale): string => {
  const label = statuses[text] ?? text;
  return locale === 'ru'
    ? label
    : ((labels[label] ?? studyLiteralLabels[label])?.[locale === 'kz' ? 0 : 1] ?? label);
};

export const useStudyText = () => {
  const locale = useContext(I18nContext)?.locale ?? 'ru';
  return (text: string) => studyText(text, locale);
};
